import { DefaultOptionType } from "antd/es/select";
import Flex from "antd/es/flex";
import Image from "antd/es/image";
import { throwIfFalsy } from "throw-expression";
import { isAddress as isAddressEth, toUnits } from "thirdweb";
import bs58check from "bs58check";
import { checkAddress } from "@polkadot/util-crypto";
import { PublicKey } from "@solana/web3.js";
import { Chains, ExchangeToken, FormModel, ReservesType } from "./types";

export const appType = process.env.REACT_APP_LOCAL ? "local" : "prod";

export const steps = {
    "Select token": 0,
    "Create Order": 1,
    "Send tokens": 2,
    "Success!": 3,
};

export const optionsProps = {
    className: "Exchange__image",
    width: "18px",
    height: "18px",
    preview: false,
};

export const fields: { [K in keyof FormModel]-?: K } = {
    fromChain: "fromChain",
    toChain: "toChain",
    fromAmount: "fromAmount",
    toAmount: "toAmount",
    orderId: "orderId",
    recipient: "recipient",
    transactionHash: "transactionHash",
    endTransactionHash: "endTransactionHash",
    executed: "executed",
    token: "token",
};

export const maxDecimals = 18;

export const decimals: Record<Chains, Record<ExchangeToken, number>> = {
    Ethereum: {
        LLD: 18,
        USDT: 6,
    },
    Solana: {
        LLD: 8,
        USDT: 6,
    },
    Tron: {
        LLD: 18,
        USDT: 6,
    },
    Liberland: {
        LLD: 12,
        USDT: 18,
    },
};

export const options: (Omit<DefaultOptionType, "value"> & { value: Chains })[] = [
    {
        value: "Ethereum",
        label: (
            <Flex gap="10px" align="center" className="Exchange__image-container">
                <Image src={require("../ethereum.svg").default} {...optionsProps} />
                Ethereum
            </Flex>
        ),
    },
    {
        value: "Solana",
        label: (
            <Flex gap="10px" align="center" className="Exchange__image-container">
                <Image src={require("../solana.svg").default} {...optionsProps} />
                Solana
            </Flex>
        ),
    },
    {
        value: "Tron",
        label: (
            <Flex gap="10px" align="center" className="Exchange__image-container">
                <Image src={require("../tron.svg").default} {...optionsProps} />
                Tron
            </Flex>
        ),
    },
    {
        value: "Liberland",
        label: (
            <Flex gap="10px" align="center" className="Exchange__image-container">
                <Image src={require("../logo-small.png")} {...optionsProps} />
                Liberland
            </Flex>
        ),
    },
];

export const walletsValidator: Record<Chains, (address: string) => void> = {
    Ethereum: (address) => throwIfFalsy(isAddressEth(address), "Invalid address"),
    Solana: (address) => {
        try {
            new PublicKey(address);
        } catch {
            throw new Error("Invalid address");
        }
    },
    Tron: (address) => {
        try {
            const decoded = bs58check.decode(address);
            throwIfFalsy(decoded[0] === 0x41 && decoded.length === 21, "Invalid address");
        } catch (e) {
            throw new Error("Invalid address");
        }
    },
    Liberland: (address) => throwIfFalsy(checkAddress(address, 42)[0] && address.startsWith("5"), "Invalid address"),
};

export const reservesValidator: Record<Chains, (amount: string, token: ExchangeToken, reserves: ReservesType) => void> = {
    Ethereum: (amount: string, token: ExchangeToken, reserves: ReservesType) => throwIfFalsy(toUnits(amount, decimals.Ethereum[token]) < reserves.eth, "Not enough LLD on Ethereum in reserves"),
    Solana: (amount: string, token: ExchangeToken, reserves: ReservesType) => throwIfFalsy(toUnits(amount, decimals.Solana[token]) < reserves.sol, "Not enough LLD on Solana in reserves"),
    Tron: (amount: string, token: ExchangeToken, reserves: ReservesType) => throwIfFalsy(toUnits(amount, decimals.Tron[token]) < reserves.trx, "Not enough LLD on Tron in reserves"),
    Liberland: (amount: string, token: ExchangeToken, reserves: ReservesType) => throwIfFalsy(toUnits(amount, decimals.Liberland[token]) < reserves.ll, "Not enough LLD on Liberland chain in reserves"),
};

export const reservesBreakpoints = {
    gutter: 16,
    xs: 1,
    sm: 2,
    md: 2,
    lg: 2,
    xl: 2,
    xxl: 2,
};

export const secondsLimit = 30;

const COINBASE = "com.coinbase.wallet";
const METAMASK = "io.metamask";
const RAINBOW = "me.rainbow";
const ZERION = "io.zerion.wallet";
const RABBY = "io.rabby";
export const thirdwebWallets = [
    COINBASE,
    METAMASK,
    RAINBOW,
    ZERION,
    RABBY,
] as const;

